import React, { useEffect, useState } from 'react';
import { getPartnerships, Partnership, Page } from '../api/admin';
import styles from './CommonPage.module.css';
import { X, Search, ChevronLeft, ChevronRight } from 'lucide-react';

const PartnershipPage: React.FC = () => {
    const [data, setData] = useState<Page<Partnership> | null>(null);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [selectedInquiry, setSelectedInquiry] = useState<Partnership | null>(null);
    const [showModal, setShowModal] = useState(false);

    useEffect(() => {
        fetchPartnerships();
    }, [page]);

    const fetchPartnerships = async () => {
        setLoading(true);
        try {
            const result = await getPartnerships(page, 20);
            setData(result);
        } catch (error) {
            console.error('Failed to fetch partnerships', error);
        } finally {
            setLoading(false);
        }
    };

    const handlePrev = () => {
        if (page > 0) setPage(page - 1);
    };

    const handleNext = () => {
        if (data && page < data.totalPages - 1) setPage(page + 1);
    };

    const handleShowDetail = (inquiry: Partnership) => {
        setSelectedInquiry(inquiry);
        setShowModal(true);
    };

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <div className={styles.wrapper}>
                    <h1>제휴 문의</h1>
                    <p>접수된 제휴 문의 내역을 확인합니다.</p>
                </div>
            </div>

            <div className={styles.tableContainer}>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th>회사명</th>
                            <th>담당자</th>
                            <th>연락처</th>
                            <th>이메일</th>
                            <th>제휴 유형</th>
                            <th>날짜</th>
                            <th>내용</th>
                            <th>관리</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={8} style={{ textAlign: 'center', padding: '24px' }}>로딩 중...</td></tr>
                        ) : data?.content.map((item: Partnership) => (
                            <tr key={item.id}>
                                <td data-label="회사명" style={{ fontWeight: 500 }}>{item.companyName}</td>
                                <td data-label="담당자">{item.contactName}</td>
                                <td data-label="연락처" style={{ fontSize: '0.9rem', color: '#6b7280' }}>{item.phone}</td>
                                <td data-label="이메일">{item.email}</td>
                                <td data-label="유형">
                                    <span style={{
                                        padding: '2px 8px',
                                        borderRadius: '12px',
                                        fontSize: '0.75rem',
                                        fontWeight: 600,
                                        backgroundColor: '#eff6ff',
                                        color: '#1d4ed8'
                                    }}>
                                        {item.type === 'strategic' ? '전략적 파트너십' :
                                            item.type === 'reseller' ? '리셀러 파트너' :
                                                item.type === 'technology' ? '기술 제휴' :
                                                    item.type === 'marketing' ? '마케팅 제휴' : '기타'}
                                    </span>
                                </td>
                                <td data-label="날짜" style={{ fontSize: '0.85rem' }}>{new Date(item.createdAt).toLocaleString()}</td>
                                <td data-label="내용" style={{ maxWidth: '200px' }}>
                                    <div style={{
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        cursor: 'pointer',
                                        color: '#4b5563'
                                    }} onClick={() => handleShowDetail(item)}>
                                        {item.message}
                                    </div>
                                </td>
                                <td data-label="관리">
                                    <button
                                        className={styles.primaryBtn}
                                        style={{ padding: '4px 10px', fontSize: '0.8rem' }}
                                        onClick={() => handleShowDetail(item)}
                                    >
                                        상세보기
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {data?.content.length === 0 && !loading && (
                            <tr><td colSpan={8} style={{ textAlign: 'center', padding: '24px', color: '#6b7280' }}>문의 내역이 없습니다.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            <div className={styles.pagination}>
                <button onClick={handlePrev} disabled={page === 0}>
                    <ChevronLeft size={16} /> 이전
                </button>
                <span>
                    {data?.number !== undefined ? data.number + 1 : 1} / {data?.totalPages || 1} 페이지
                </span>
                <button onClick={handleNext} disabled={!data || page >= data.totalPages - 1}>
                    다음 <ChevronRight size={16} />
                </button>
            </div>

            {showModal && selectedInquiry && (
                <div className={styles.modalOverlay}>
                    <div className={styles.modal} style={{ maxWidth: '600px', width: '90%' }}>
                        <div className={styles.modalHeader}>
                            <h3>제휴 문의 상세</h3>
                            <button onClick={() => setShowModal(false)} className={styles.closeBtn}>
                                <X size={20} />
                            </button>
                        </div>
                        <div className={styles.modalBody}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
                                <div>
                                    <label style={{ fontSize: '0.85rem', color: '#6b7280', display: 'block', marginBottom: '4px' }}>회사명</label>
                                    <div style={{ fontWeight: 500 }}>{selectedInquiry.companyName}</div>
                                </div>
                                <div>
                                    <label style={{ fontSize: '0.85rem', color: '#6b7280', display: 'block', marginBottom: '4px' }}>담당자</label>
                                    <div>{selectedInquiry.contactName}</div>
                                </div>
                                <div>
                                    <label style={{ fontSize: '0.85rem', color: '#6b7280', display: 'block', marginBottom: '4px' }}>연락처</label>
                                    <div>{selectedInquiry.phone}</div>
                                </div>
                                <div>
                                    <label style={{ fontSize: '0.85rem', color: '#6b7280', display: 'block', marginBottom: '4px' }}>이메일</label>
                                    <div>{selectedInquiry.email}</div>
                                </div>
                                <div>
                                    <label style={{ fontSize: '0.85rem', color: '#6b7280', display: 'block', marginBottom: '4px' }}>제휴 유형</label>
                                    <div>
                                        {selectedInquiry.type === 'strategic' ? '전략적 파트너십' :
                                            selectedInquiry.type === 'reseller' ? '리셀러 파트너' :
                                                selectedInquiry.type === 'technology' ? '기술 제휴' :
                                                    selectedInquiry.type === 'marketing' ? '마케팅 제휴' : '기타'}
                                    </div>
                                </div>
                                <div>
                                    <label style={{ fontSize: '0.85rem', color: '#6b7280', display: 'block', marginBottom: '4px' }}>접수 일시</label>
                                    <div>{new Date(selectedInquiry.createdAt).toLocaleString()}</div>
                                </div>
                            </div>

                            <div>
                                <label style={{ fontSize: '0.85rem', color: '#6b7280', display: 'block', marginBottom: '8px' }}>문의 내용</label>
                                <div style={{
                                    padding: '16px',
                                    backgroundColor: '#f9fafb',
                                    borderRadius: '8px',
                                    border: '1px solid #e5e7eb',
                                    whiteSpace: 'pre-wrap',
                                    lineHeight: '1.6',
                                    minHeight: '150px',
                                    maxHeight: '400px',
                                    overflowY: 'auto'
                                }}>
                                    {selectedInquiry.message}
                                </div>
                            </div>

                            <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'flex-end' }}>
                                <button
                                    className={styles.primaryBtn}
                                    onClick={() => setShowModal(false)}
                                    style={{ padding: '8px 24px' }}
                                >
                                    확인
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PartnershipPage;
